import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { ITrend } from '../../../interfaces/Trend';
import { EvTabs, VIEW_MODES } from '../../common/ev-tabs/EvTabs';
import { SeverityTrendChart } from '../severity-trend-chart/SeverityTrendChart';

import { SEVERITIES_CHART_TYPE } from './SeverityTrendTypes';

import './SeverityTrends.scss';

interface ISeverityTrends {
	scanTrendsData: ITrend[];
	setChartType: (type: SEVERITIES_CHART_TYPE) => void;
	chartType: SEVERITIES_CHART_TYPE;
	onBarItemClick: (scanId: string, severity: string) => void;
}

const tabOptions = [
	{ key: SEVERITIES_CHART_TYPE.TOTAL_ISSUES, label: 'Total Issues' },
	{ key: SEVERITIES_CHART_TYPE.PERCENTAGE, label: 'Percentage' },
	{ key: SEVERITIES_CHART_TYPE.AVERAGE_PER_PAGE, label: 'Avg Per Page' }
];

// this function calculates positioning of Tabs,
// since it should be in the middle between chart title and filters
const getStylesForTabs = (): React.CSSProperties | null => {
	try {
		const chartElement = document.querySelector('.severity-trend-chart') as HTMLElement;
		const filtersElement = document.querySelector('.highcharts-legend') as HTMLElement;

		if (!chartElement || !filtersElement) {
			return null;
		}

		const chartWidth = chartElement.clientWidth;
		const filtersWidth = filtersElement.clientWidth;
		const titleWidth = 152;
		const tabsWidth = 274;
		const emptySpace = chartWidth - (filtersWidth + titleWidth + tabsWidth);
		const leftPosition = emptySpace / 2 + 30;

		return { position: 'absolute', left: leftPosition, top: 30 };
	} catch (error) {
		return null;
	}
};

export const SeverityTrends: FC<ISeverityTrends> = ({
	scanTrendsData,
	chartType,
	setChartType,
	onBarItemClick
}: ISeverityTrends) => {
	const [tabsStyles, setTabStyles] = useState<{ [key: string]: React.CSSProperties } | null>(null);
	const containerRef = useRef<HTMLDivElement>(null);
	const resizeObserverRef = useRef<ResizeObserver | null>(null);

	const updateTabStyles = useCallback(() => {
		const styles = getStylesForTabs();
		if (styles) {
			setTabStyles({ wrapper: styles });
		}
	}, []);

	useEffect(() => {
		// Set up ResizeObserver to recalculate when container size changes
		resizeObserverRef.current = new ResizeObserver(() => {
			updateTabStyles();
		});

		if (containerRef.current) {
			resizeObserverRef.current.observe(containerRef.current);
		}

		return () => {
			if (resizeObserverRef.current) {
				resizeObserverRef.current.disconnect();
			}
		};
	}, [updateTabStyles]);

	// Initial calculation - needed because child components might render
	// their DOM elements without triggering ResizeObserver
	useEffect(() => {
		const timeoutId = setTimeout(updateTabStyles, 100);
		return () => clearTimeout(timeoutId);
	}, [updateTabStyles]);

	// Recalculate styles when data changes (might affect chart size)
	useEffect(() => {
		const timeoutId = setTimeout(updateTabStyles, 200);
		return () => clearTimeout(timeoutId);
	}, [scanTrendsData, updateTabStyles]);

	const severityTrends = useMemo(() => {
		return tabOptions.map((option) => (
			<SeverityTrendChart
				key={option.key}
				scanTrendsData={scanTrendsData}
				chartType={option.key}
				onBarItemClick={onBarItemClick}
			/>
		));
	}, [scanTrendsData, onBarItemClick]);

	const renderTabsSelection = useCallback(
		() => (
			<EvTabs
				className="trends-charts-tabs"
				options={tabOptions}
				selected={chartType}
				onSelect={setChartType}
				tabPanels={severityTrends}
				styles={tabsStyles}
				viewMode={VIEW_MODES.DIVIDED}
			/>
		),
		[chartType, setChartType, severityTrends, tabsStyles]
	);

	return (
		<div ref={containerRef} className="severity-trends">
			{renderTabsSelection()}
		</div>
	);
};
